import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Loader2, Clock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const sessionExpired = sp.get("session") === "expired";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.toLowerCase().trim(), password);
      if (u.role === "admin") nav("/admin");
      else if (u.role === "accounts") nav("/accounts");
      else nav("/timesheet");
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen relative grain overflow-hidden flex items-center justify-center px-6">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 dark:block hidden" style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(168,85,247,0.10), transparent 55%)" }} />
        <div className="absolute inset-0 dark:hidden" style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(59,130,246,0.10), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(168,85,247,0.06), transparent 55%)" }} />
        <div className="absolute inset-0 opacity-30 dark:block hidden" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="absolute inset-0 opacity-40 dark:hidden" style={{ backgroundImage: "linear-gradient(rgba(15,23,42,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      </div>

      <div className="grid md:grid-cols-2 gap-12 max-w-5xl w-full items-center">
        <div className="hidden md:block fade-up">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-blue-600 dark:text-blue-600 dark:text-blue-400/80 mb-6">— Internal Tool</p>
          <h1 className="font-display text-5xl lg:text-6xl tracking-tight font-light leading-[1.05] mb-6">
            Engineering<br />clarity in your<br /><span className="italic font-thin text-blue-600 dark:text-blue-600 dark:text-blue-400">timesheet.</span>
          </h1>
          <p className="text-muted-foreground max-w-md leading-relaxed">
            A precise instrument to log presence, leave, and virtual hours — built for the Dataeko team.
          </p>
          <div className="mt-12 grid grid-cols-4 gap-2 max-w-md">
            {["P","PH","WFH","WFHH","PTO","PTOH","LOP","LOPH","WO","HO"].map(c => (
              <div key={c} className="aspect-square rounded-md border border-border flex items-center justify-center font-mono text-xs text-foreground/90 bg-muted/30">
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="fade-up">
          <div className="bg-card/80 backdrop-blur-xl border border-border rounded-2xl p-8 sm:p-10">
            <Logo size="lg" className="mb-8" />
            <h2 className="font-display text-2xl mb-1 font-medium">Welcome back</h2>
            <p className="text-sm text-muted-foreground mb-6">Sign in with your @dataeko.ai account</p>

            {sessionExpired && (
              <div className="mb-5 flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs" data-testid="session-expired-banner">
                <Clock className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Your session expired after 15 minutes of inactivity. Please sign in again.</span>
              </div>
            )}

            <Button type="button" onClick={googleSignIn} variant="outline"
              className="w-full h-11 mb-5 bg-white text-slate-900 hover:bg-slate-100 border-slate-200 font-medium dark:border-border"
              data-testid="google-signin-button">
              <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground">or password</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email</Label>
                <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@dataeko.ai"
                  className="mt-2 bg-background border-border h-11"
                  data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Password</Label>
                <Input id="password" type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="mt-2 bg-background border-border h-11"
                  data-testid="login-password-input" />
              </div>

              {err && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md p-3" data-testid="login-error">{err}</div>}

              <Button type="submit" disabled={busy} className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-medium" data-testid="login-submit-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-border/70 text-xs text-muted-foreground leading-relaxed font-mono">
              Need access? Ask your admin to create your account.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
